<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { SatButtonMoney } from "@chatfood/design-system";
import { BasketIconBadge } from "../basket-icon-badge";
import { computed, ref, onMounted } from "vue";
import { useModalHubStore } from "../modal-hub";
import { GuestPrice } from "../guest-price";
import { useBasketStore } from "~/stores/basket";
import { useBusinessStore } from "~/stores/business";
import { useOutletStore } from "~/stores/outlet";
import { useLocale } from "~/composables/use-locale";
import { useGuestStore } from "~/stores/guest";
import { useFeatureMatrixStore } from "~/stores/featureMatrix";
import { useCustomerStore } from "~/stores/customer";
import { useNavigation } from "~/composables/use-navigation";
import { useFeatureFlag } from "#imports";

const css = bemBuilder("basket-view-checkout");

const { t } = useLocale();
const { isEnabled } = useFeatureFlag();

const business = useBusinessStore();
const basket = useBasketStore();
const outlet = useOutletStore();
const modalHub = useModalHubStore();
const customer = useCustomerStore();
const guest = useGuestStore();
const featureMatrix = useFeatureMatrixStore();
const { push } = useNavigation();

const hasCrossSellFlow = ref(false);

const isDisabled = computed<boolean>(() => {
  if (outlet.isClosed) return true;

  if (
    outlet.orderingMode.outletState === "pre_order_only" &&
    !basket.scheduledTime
  ) {
    return true;
  }

  if (basket.isForDelivery && !basket.addressId && !basket.currentLocation) {
    return true;
  }

  return false;
});

const resolveFeatureFlags = async () => {
  hasCrossSellFlow.value =
    featureMatrix.isToggleEnabled("enable-floating-items-cross-sell") &&
    (await isEnabled("direct.useBasketCrossSell"));
};

onMounted(() => {
  if (!basket.isLoaded) {
    throw new Error(
      "BasketViewCheckout component requires basket store to be loaded.",
    );
  }

  resolveFeatureFlags();
});

const goToCrossSell = async () => {
  if (hasCrossSellFlow.value) {
    modalHub.showCrossSellList();
    return;
  }

  await push("checkout", {});
};

const onCheckout = async () => {
  if (!customer.isLogged && !featureMatrix.skipAuthFlow) {
    modalHub.showAuthManager(async () => {
      if (!customer.isLogged && !guest.startedFlow) return;
      if (
        basket.orderingMode === "delivery" &&
        !basket.addressId &&
        !guest.address
      ) {
        modalHub.showAddressForm(async () => {
          if (basket.addressId || guest.address) {
            await goToCrossSell();
          }
        });
        return;
      }

      await goToCrossSell();
    }, true);
    return;
  }
  if (basket.orderingMode === "delivery") {
    if (!basket.addressId && customer.isLogged) {
      modalHub.showAddressForm(async () => {
        if (basket.addressId) {
          await goToCrossSell();
        }
      });
      return;
    }
    if (!guest.address && !customer.isLogged) {
      modalHub.showAddressForm(async () => {
        if (guest.address) {
          await goToCrossSell();
        }
      });
      return;
    }
    const address = computed(() => {
      return customer.addresses.find(
        (address) => address.id === basket.addressId,
      );
    });
    if (business.config.isPostalCodeRequired && !address.value?.zipCode) {
      modalHub.showAddressForm(async () => {
        if (basket.addressId && address.value?.zipCode) {
          await goToCrossSell();
        }
      });
      return;
    }
  }

  await goToCrossSell();
};
</script>

<template>
  <div :class="css()">
    <div :class="css('cta-row')">
      <div :class="css('cart-icon')">
        <BasketIconBadge />
      </div>
      <SatButtonMoney
        :class="css('button')"
        :price="basket.totalBreakdown.outstandingBalance"
        :locale="business.locale"
        :disabled="isDisabled"
        size="large"
        :hide-cents="false"
        @click="onCheckout"
      >
        {{ t("components.basket_view_checkout.cta") }}
      </SatButtonMoney>
    </div>
    <GuestPrice />
  </div>
</template>

<style lang="scss" scoped>
.basket-view-checkout {
  display: flex;
  gap: var(--spacing-xs);
  flex-direction: column;

  &__cta-row {
    display: flex;
    align-items: center;
    gap: var(--spacing-md);
  }

  &__cart-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 44px;
    padding-top: 4px;

    @media (width > $breakpoint-sm) {
      display: none;
    }
  }

  &__button {
    flex: 1;
    min-width: 0;
  }
}
</style>
